import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { Page, Pagebar, PagebarActions, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { ControlledTable } from '@monorepo/controlled/src/components/table-wrapper/table-wrapper';
import { ControlledDater } from '@monorepo/controlled/src/components/controlled-dater/controlled-dater';
import { IMFPStore } from '../../../../modules/stores';
import {
	Entities,
	IPerformanceOptionsFilterType,
	PerformancesColumns,
	useReportsList,
} from '../../../../modules/hooks/apis/use-reports-list';
import styles from './../../../../styles/pages.module.scss';
import { useMetricsGet } from '../../../../modules/hooks/tools/use-metrics-get';
import { useMetrics } from '../../../../modules/hooks/tools/use-metrics';
import { availablePerformanceToMetrics } from '../../../../modules/models/performance.model';
import { SpacerY } from '@monorepo/base/src/components/spacer/spacer';
import { useAdvertiserActions } from '../../../../modules/hooks/actions/advertiser.actions';
import { EntityActionsMenu } from '../../../../modules/components/entity-actions-menu/entity-actions-menu';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { useChannelActions } from '../../../../modules/hooks/actions/channel.actions';
import { useChannelHeaders } from '../../../../modules/hooks/theaders/channel.headers';
import { ManifestoChart } from '../../../../modules/components/chart/manifesto-chart';
import { Helmet } from 'react-helmet-async';
import { PaginationState } from '@tanstack/react-table';
import { isFetchFromBackend } from '../../../../modules/helpers/reports.helpers';
import { isAbortError } from '@monorepo/tools/src/lib/tools/ask/guards';

export const AdvertiserChannels = observer(() => {
	const { channelStore, advertiserStore } = useStores<IMFPStore>();
	const { advertiserId } = useParams();
	const { currentRouteWithoutSpecialChars } = useRoute();

	const reportsStore = channelStore.getReportsStore();
	const reportData = reportsStore.getData();
	const metricsStore = channelStore.getMetricsStore();
	const currentSegments = channelStore.getSegments();
	const advertiserName = advertiserStore.getCrud().getData().getName();

	if (!advertiserId) {
		return null;
	}

	const { reportsError, fetchReports } = useReportsList(
		reportsStore,
		{
			entity: Entities.Channels,
			filters: [
				{
					column: PerformancesColumns.AdvertiserId,
					filterType: IPerformanceOptionsFilterType.Equals,
					value: [advertiserId],
				},
			],
			groupBys: [PerformancesColumns.Channel],
		},
		undefined,
		currentSegments
	);

	const { metricsError, fetchMetrics } = useMetricsGet(metricsStore, {
		entity: Entities.Chart,
		groupBys: [PerformancesColumns.Date],
		filters: [
			{
				column: PerformancesColumns.AdvertiserId,
				filterType: IPerformanceOptionsFilterType.Include,
				value: [advertiserId],
			},
		],
	});

	const { columns } = useChannelHeaders({ summary: reportData?.getSummary() });
	const { FiltersActions, SegmentActions } = useChannelActions({ currentSegments });
	const { metrics, xLabels } = useMetrics(metricsStore.getData());
	const { EntityActions } = useAdvertiserActions();
	const advertiser = advertiserStore.getCrud().getData(); // This advertiser will be stored from breadcrumbs

	return (
		<Page unstyled={true} className={styles.article}>
			<Helmet>
				<title>{advertiserName || 'Advertiser'} - Channels</title>
			</Helmet>
			<Pagebar classes={{ pagebar: styles.padding }}>
				<PagebarTitle>Channels</PagebarTitle>
				<PagebarActions>
					<ControlledDater />
					<EntityActionsMenu title={'Advertiser Actions'}>
						<EntityActions entity={advertiser} />
					</EntityActionsMenu>
				</PagebarActions>
			</Pagebar>
			<ManifestoChart
				isLoading={metricsStore.getIsLoading()}
				metrics={metrics}
				labels={xLabels}
				legendOptions={Object.keys(availablePerformanceToMetrics)}
				isError={!!metricsError && !isAbortError(metricsError)}
			/>
			<SpacerY y={2} />
			<ControlledTable
				currentRoute={currentRouteWithoutSpecialChars}
				columns={columns}
				classes={{ wrapper: styles.padding }}
				debugProps={{ dataAttrs: [new DataAttribute('id', 'channels_advertiser_table')] }}
				data={reportData?.getData() || []}
				isLoading={reportsStore.getIsLoading()}
				isWithPerformance={true}
				isLoadingPerformance={reportsStore.getIsLoading()}
				fetchReports={fetchReports}
				fetchMetrics={fetchMetrics}
				onPageChange={(state: PaginationState) => {
					if (isFetchFromBackend(reportData, state) && !reportsStore.getIsLoading()) {
						fetchReports({ appendData: true, resetOffset: false });
					}
				}}
				defaultSortBy={[{ id: 'revenue', desc: true }]}
				isError={!!reportsError && !isAbortError(reportsError)}>
				{() => {
					return {
						FiltersActions,
						SegmentActions,
					};
				}}
			</ControlledTable>
		</Page>
	);
});
